import { EmailComposer } from "../components/EmailComposer";
import { toast } from "../components/Toast";
import { DateTimeInput } from "../components/DateInput";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiBlob } from "../lib/api";
import { useOrg } from "../lib/session";
import { ago, contactName, dateTime, initials, label, sourceLabel, stageLabel, stages } from "../lib/format";
import type { Activity, Contact, ContactDetail, Member, Task, Thread } from "../lib/types";
import { Empty, ErrorText, Field, Form, Loading, PageHeader, str } from "../components/ui";
import { money, useEntitlements } from "../lib/plans";
import { NewDeal, type Deal } from "./deals";
import type { Appointment, WaTemplate } from "../lib/types";
import { registerWarmer } from "../lib/smooth";

export interface ContactFilters { q: string; stage: string; owner: string; tag: string; sort: string; dir: "asc" | "desc" }
const blankFilters: ContactFilters = { q: "", stage: "", owner: "", tag: "", sort: "created", dir: "desc" };
const PAGE = 50;

const contactsQuery = (orgId: string, base: string, f: ContactFilters) => {
  const params = new URLSearchParams({ ...(f.q && { q: f.q }), ...(f.stage && { stage: f.stage }), ...(f.owner && { owner: f.owner }), ...(f.tag && { tag: f.tag }),
    sort: f.sort, dir: f.dir, limit: String(PAGE) });
  return {
    queryKey: [orgId, "contacts", f],
    queryFn: ({ pageParam }: { pageParam: number }) =>
      api<{ contacts: Contact[]; total?: number }>(`${base}/contacts?${params}&offset=${pageParam}${pageParam === 0 ? "&count=1" : ""}`),
    initialPageParam: 0,
    getNextPageParam: (last: { contacts: Contact[] }, pages: { contacts: Contact[] }[]) => {
      const loaded = pages.reduce((n, p) => n + p.contacts.length, 0);
      return last.contacts.length === PAGE && loaded < 10000 ? loaded : undefined;
    },
  };
};
registerWarmer("contacts", (qc, { orgId, base }) => { qc.prefetchInfiniteQuery({ ...contactsQuery(orgId, base, blankFilters), staleTime: 10_000 }); });
import { TemplateSender } from "../components/WhatsAppTemplates";
import { CustomFields } from "../components/CustomFields";
import { useWords } from "../lib/workspace";

interface SavedView { id: string; name: string; filters: Partial<ContactFilters> }

export function Contacts() {
  const { base, orgId, allows } = useOrg();
  const qc = useQueryClient();
  const [f, setF] = useState<ContactFilters>(blankFilters);
  const [search, setSearch] = useState("");
  const [adding, setAdding] = useState(false);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const navigate = useNavigate();
  useEffect(() => { const t = setTimeout(() => setF((x) => (x.q === search.trim() ? x : { ...x, q: search.trim() })), 250); return () => clearTimeout(t); }, [search]);
  useEffect(() => { setPicked(new Set()); }, [f]);
  const list = useInfiniteQuery(contactsQuery(orgId, base, f));
  const rows = list.data?.pages.flatMap((p) => p.contacts) ?? [];
  const total = list.data?.pages[0]?.total;
  const tags = useQuery({ queryKey: [orgId, "contact-tags"], queryFn: () => api<{ tags: { name: string; count: number }[] }>(`${base}/contacts/tags`), staleTime: 30_000 });
  const views = useQuery({ queryKey: [orgId, "contact-views"], queryFn: () => api<{ views: SavedView[] }>(`${base}/contacts/views`) });
  const w = useWords();
  const plan = useEntitlements();
  const canWrite = !allows("readonly");
  const set = (patch: Partial<ContactFilters>) => setF((x) => ({ ...x, ...patch }));
  const sortBy = (key: string) => setF((x) => ({ ...x, sort: key, dir: x.sort === key ? (x.dir === "asc" ? "desc" : "asc") : key === "name" || key === "owner" ? "asc" : "desc" }));
  const filtered = f.q || f.stage || f.owner || f.tag;
  const activeView = views.data?.views.find((v) => JSON.stringify({ ...blankFilters, ...v.filters }) === JSON.stringify(f));
  const head = (key: string, text: string, cls?: string) => (
    <th className={cls} aria-sort={f.sort === key ? (f.dir === "asc" ? "ascending" : "descending") : undefined}>
      <button type="button" className={`th-sort${f.sort === key ? " on" : ""}`} onClick={() => sortBy(key)}>{text}<span aria-hidden>{f.sort === key ? (f.dir === "asc" ? " ↑" : " ↓") : ""}</span></button>
    </th>
  );
  const toggle = (id: string) => setPicked((p) => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const allPicked = rows.length > 0 && rows.every((c) => picked.has(c.id));
  return (
    <>
      <PageHeader title={w("contact", true)} actions={canWrite && (
        <>
          {allows("import") && plan.enabled("contact_import") && <Link className="button-link secondary" to={`/o/${orgId}/contacts/import`}>Import</Link>}
          <button onClick={() => setAdding((a) => !a)}>{adding ? "Cancel" : `Add ${w.lower("contact")}`}</button>
        </>
      )} />
      {adding && (
        <section className="card">
          <Form
            submitLabel={`Create ${w.lower("contact")}`}
            onSubmit={async (d) => {
              const c = await api<Contact>(`${base}/contacts`, {
                method: "POST",
                json: { first_name: str(d, "first_name"), last_name: str(d, "last_name"), email: str(d, "email"), phone: str(d, "phone") },
              });
              navigate(`/o/${orgId}/contacts/${c.id}`);
            }}
          >
            <div className="row">
              <Field label="First name"><input name="first_name" maxLength={100} autoFocus /></Field>
              <Field label="Last name"><input name="last_name" maxLength={100} /></Field>
            </div>
            <div className="row">
              <Field label="Email"><input name="email" type="email" maxLength={254} placeholder="name@example.com" /></Field>
              <Field label="Phone (with country code)"><input name="phone" type="tel" maxLength={32} placeholder="+973 3300 0000" /></Field>
            </div>
          </Form>
        </section>
      )}
      <ViewsBar views={views.data?.views ?? []} active={activeView?.id} filters={f} canSave={!!filtered || f.sort !== "created"}
        onPick={(v) => { setSearch(v?.filters.q ?? ""); setF(v ? { ...blankFilters, ...v.filters } : blankFilters); }} />
      <div className="filters">
        <input type="search" placeholder="Search name, email, phone" value={search} maxLength={100} onChange={(e) => setSearch(e.target.value)} aria-label={`Search ${w.lower("contact", true)}`} />
        <select value={f.stage} onChange={(e) => set({ stage: e.target.value })} aria-label="Stage">
          <option value="">All stages</option>
          {stages.map((s) => <option key={s} value={s}>{stageLabel[s]}</option>)}
        </select>
        <select value={f.owner} onChange={(e) => set({ owner: e.target.value })} aria-label="Owner">
          <option value="">Any owner</option>
          <option value="me">Mine</option>
          <option value="unassigned">Unassigned</option>
        </select>
        {(tags.data?.tags.length ?? 0) > 0 && (
          <select value={f.tag} onChange={(e) => set({ tag: e.target.value })} aria-label="Tag">
            <option value="">Any tag</option>
            {tags.data!.tags.map((t) => <option key={t.name} value={t.name}>{t.name} ({t.count})</option>)}
          </select>
        )}
        {filtered && <button type="button" className="link small" onClick={() => { setSearch(""); setF({ ...blankFilters, sort: f.sort, dir: f.dir }); }}>Clear filters</button>}
        <span className="spacer" />
        {total !== undefined && <span className="muted small">{total.toLocaleString()} {total === 1 ? w.lower("contact") : w.lower("contact", true)}</span>}
      </div>
      {canWrite && picked.size > 0 && (
        <BulkBar ids={[...picked]} onDone={() => { setPicked(new Set()); qc.invalidateQueries({ queryKey: [orgId, "contacts"] }); qc.invalidateQueries({ queryKey: [orgId, "contact-tags"] }); }} />
      )}
      {list.isLoading ? <Loading rows={6} /> : list.error ? <ErrorText error={list.error} /> : rows.length === 0 ? (
        <Empty>{filtered ? `No ${w.lower("contact", true)} match.` : `No ${w.lower("contact", true)} yet. Add one, import a list, or let your AI agent bring them in.`}</Empty>
      ) : (
        <>
          <table className="table contacts-table">
            <thead>
              <tr>
                {canWrite && <th className="pick"><input type="checkbox" aria-label="Select all shown" checked={allPicked} onChange={() => setPicked(allPicked ? new Set() : new Set(rows.map((c) => c.id)))} /></th>}
                {head("name", "Name")}{head("stage", "Stage")}{head("score", "Score", "num")}{head("owner", "Owner")}<th>Source</th>{head("last_engaged", "Last engaged")}
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id} className={picked.has(c.id) ? "picked" : undefined}>
                  {canWrite && <td className="pick"><input type="checkbox" aria-label={`Select ${contactName(c)}`} checked={picked.has(c.id)} onChange={() => toggle(c.id)} /></td>}
                  <td>
                    <Link to={`/o/${orgId}/contacts/${c.id}`} className="row-link" dir="auto">{contactName(c)}</Link>
                    <div className="muted small">{c.email ?? c.phone ?? ""}</div>
                    {(c.tags?.length ?? 0) > 0 && <div className="tag-row">{c.tags!.slice(0, 3).map((t) => <span key={t} className="ctag">{t}</span>)}{c.tags!.length > 3 && <span className="muted small">+{c.tags!.length - 3}</span>}</div>}
                  </td>
                  <td><span className={`stage-chip stage-${c.lifecycle_stage}`}>{stageLabel[c.lifecycle_stage] ?? c.lifecycle_stage}</span></td>
                  <td className="num">{c.lead_score}</td>
                  <td>{c.owner_name ?? <span className="muted">Unassigned</span>}</td>
                  <td className="small">{label(sourceLabel, c.source)}</td>
                  <td className="small nowrap">{c.last_engaged_at ? ago(c.last_engaged_at) : <span className="muted">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <ul className="contact-cards">
            {rows.map((c) => (
              <li key={c.id}>
                {canWrite && <input type="checkbox" aria-label={`Select ${contactName(c)}`} checked={picked.has(c.id)} onChange={() => toggle(c.id)} />}
                <Link to={`/o/${orgId}/contacts/${c.id}`}>
                  <span className="avatar" aria-hidden>{initials(contactName(c))}</span>
                  <span className="grow">
                    <b dir="auto">{contactName(c)}</b>
                    <small className="muted">{[stageLabel[c.lifecycle_stage], c.owner_name ?? "Unassigned", c.last_engaged_at && ago(c.last_engaged_at)].filter(Boolean).join(" · ")}</small>
                  </span>
                  <span className="num muted">{c.lead_score}</span>
                </Link>
              </li>
            ))}
          </ul>
        </>
      )}
      {list.hasNextPage && <button className="secondary" onClick={() => list.fetchNextPage()} disabled={list.isFetchingNextPage}>{list.isFetchingNextPage ? "Loading…" : "Load more"}</button>}
    </>
  );
}

/** ViewsBar: saved filters, each person's own. */
function ViewsBar({ views, active, filters, canSave, onPick }: { views: SavedView[]; active?: string; filters: ContactFilters; canSave: boolean; onPick: (v: SavedView | null) => void }) {
  const { base, orgId } = useOrg();
  const qc = useQueryClient();
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState("");
  const [error, setError] = useState<unknown>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: [orgId, "contact-views"] });
  if (views.length === 0 && !canSave) return null;
  return (
    <div className="views-bar" role="toolbar" aria-label="Saved views">
      <button type="button" className={`chip${!active ? " on" : ""}`} onClick={() => onPick(null)}>All</button>
      {views.map((v) => (
        <span key={v.id} className={`chip${active === v.id ? " on" : ""}`}>
          <button type="button" onClick={() => onPick(v)}>{v.name}</button>
          {active === v.id && <button type="button" className="chip-x" aria-label={`Delete view ${v.name}`} onClick={async () => {
            if (!confirm(`Delete the view "${v.name}"?`)) return;
            try { await api(`${base}/contacts/views/${v.id}`, { method: "DELETE" }); onPick(null); refresh(); } catch (e) { setError(e); }
          }}>×</button>}
        </span>
      ))}
      {canSave && !active && (naming ? (
        <form className="inline" onSubmit={async (e) => {
          e.preventDefault(); setError(null);
          const { q, stage, owner, tag, sort, dir } = filters;
          try {
            await api(`${base}/contacts/views`, { method: "POST", json: { name: name.trim(), filters: { q, stage, owner, tag, sort, dir } } });
            setNaming(false); setName(""); refresh();
          } catch (err) { setError(err); }
        }}>
          <input aria-label="View name" placeholder="Name this view" value={name} maxLength={60} autoFocus onChange={(e) => setName(e.target.value)} style={{ width: 180 }} />
          <button type="submit" className="secondary small" disabled={!name.trim()}>Save</button>
          <button type="button" className="link small" onClick={() => setNaming(false)}>Cancel</button>
        </form>
      ) : <button type="button" className="chip add" onClick={() => setNaming(true)}>+ Save this view</button>)}
      <ErrorText error={error} />
    </div>
  );
}

/** BulkBar: change every selected contact at once. */
function BulkBar({ ids, onDone }: { ids: string[]; onDone: () => void }) {
  const { base, orgId, allows } = useOrg();
  const members = useQuery({ queryKey: [orgId, "members"], queryFn: () => api<{ members: Member[] }>(`${base}/members`), enabled: allows("contacts_all") });
  const [action, setAction] = useState("");
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [done, setDone] = useState("");
  const run = async () => {
    if (action === "archive" && !confirm(`Archive ${ids.length} ${ids.length === 1 ? "contact" : "contacts"}? They leave every list; their history is kept.`)) return;
    setBusy(true); setError(null); setDone("");
    try {
      const r = await api<{ changed: number }>(`${base}/contacts/bulk`, { method: "POST", json: { ids, action, value } });
      setDone(""); setAction(""); setValue(""); onDone(); toast(`${r.changed} ${r.changed === 1 ? "contact" : "contacts"} changed`);
    } catch (e) { setError(e); } finally { setBusy(false); }
  };
  const needsValue = action !== "" && action !== "archive" && !(action === "owner");
  return (
    <div className="bulk-bar" role="region" aria-label="Change selected contacts">
      <b>{ids.length} selected</b>
      <select aria-label="What to change" value={action} onChange={(e) => { setAction(e.target.value); setValue(""); }}>
        <option value="">Choose an action…</option>
        <option value="stage">Change stage</option>
        {allows("contacts_all") && <option value="owner">Change owner</option>}
        <option value="add_tag">Add a tag</option>
        <option value="remove_tag">Remove a tag</option>
        {allows("delete") && <option value="archive">Archive</option>}
      </select>
      {action === "stage" && (
        <select aria-label="Stage" value={value} onChange={(e) => setValue(e.target.value)}>
          <option value="">Choose a stage…</option>
          {stages.map((s) => <option key={s} value={s}>{stageLabel[s]}</option>)}
        </select>
      )}
      {action === "owner" && (
        <select aria-label="Owner" value={value} onChange={(e) => setValue(e.target.value)}>
          <option value="">Unassigned</option>
          {members.data?.members.filter((m) => m.status === "active" && m.crm_role).map((m) => <option key={m.id} value={m.id}>{m.display_name}</option>)}
        </select>
      )}
      {(action === "add_tag" || action === "remove_tag") && <input aria-label="Tag" placeholder="Tag, e.g. VIP" maxLength={40} value={value} onChange={(e) => setValue(e.target.value)} />}
      {action && <button disabled={busy || (needsValue && !value.trim())} className={action === "archive" ? "danger" : undefined} onClick={run}>{busy ? "Working…" : "Apply"}</button>}
      {done && <span className="muted small">{done}</span>}
      <ErrorText error={error} />
    </div>
  );
}

const actorLabel: Record<string, string> = { automation: "Automation", agent: "AI agent", system: "SVENEX", contact: "Customer", member: "Team" };
const kindIcon: Record<string, string> = { note: "✎", "call.logged": "☏", "meeting.logged": "◷" };

type Tab = "activity" | "conversations" | "deals" | "appointments" | "tasks";

export function ContactPage() {
  const { contactId } = useParams();
  const { base, orgId, allows } = useOrg();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const key = [orgId, "contact", contactId];
  const plan = useEntitlements();
  const w = useWords();
  const contact = useQuery({ queryKey: key, queryFn: () => api<ContactDetail>(`${base}/contacts/${contactId}`) });
  const [tab, setTab] = useState<Tab>("activity");
  const [panel, setPanel] = useState<null | "email" | "whatsapp" | "deal" | "merge">(null);
  const [menu, setMenu] = useState(false);
  const refresh = () => qc.invalidateQueries({ queryKey: key });
  const update = useMutation({
    mutationFn: (json: Record<string, unknown>) => api(`${base}/contacts/${contactId}`, { method: "PATCH", json }),
    onSuccess: () => { refresh(); qc.invalidateQueries({ queryKey: [orgId, "contacts"] }); qc.invalidateQueries({ queryKey: [orgId, "contact-tags"] }); },
  });
  const wa = useWhatsAppSender(plan.enabled("whatsapp_channel"));
  useEffect(() => { setPanel(null); setTab("activity"); }, [contactId]);

  if (contact.isLoading) return <Loading rows={6} />;
  if (contact.error) return <ErrorText error={contact.error} />;
  const c = contact.data!;
  const name = contactName(c);
  const canWrite = c.can_edit && !allows("readonly");
  const canMerge = allows("delete") && allows("contacts_all");
  const reachableWA = wa.active.length > 0 && ((c.identities ?? []).some((i) => i.channel === "whatsapp") || !!c.phone);
  const tabs: [Tab, string, boolean][] = [
    ["activity", "Activity", true],
    ["conversations", "Conversations", true],
    ["deals", w("deal", true), plan.enabled("deals")],
    ["appointments", w("appointment", true), plan.enabled("bookings")],
    ["tasks", "Tasks", true],
  ];
  return (
    <>
      <header className="contact-head">
        <span className="avatar big" aria-hidden>{initials(name)}</span>
        <div className="grow">
          <h1 dir="auto">{name}</h1>
          <p className="contact-sub">
            <span className={`stage-chip stage-${c.lifecycle_stage}`}>{stageLabel[c.lifecycle_stage] ?? c.lifecycle_stage}</span>
            <span title="Lead score, out of 100">Score {c.lead_score}</span>
            <span>{c.owner_name ? `Owner: ${c.owner_name}` : "Unassigned"}</span>
            {c.last_engaged_at && <span>Last engaged {ago(c.last_engaged_at)}</span>}
          </p>
          <TagEditor tags={c.tags ?? []} canEdit={canWrite} onSave={(tags) => update.mutateAsync({ tags })} />
        </div>
        <div className="contact-actions">
          {canWrite && reachableWA && <button className="secondary" onClick={() => setPanel(panel === "whatsapp" ? null : "whatsapp")}>WhatsApp</button>}
          {canWrite && c.email && plan.enabled("email_channel") && <button className="secondary" onClick={() => setPanel(panel === "email" ? null : "email")}>Email</button>}
          {canWrite && plan.enabled("bookings") && <Link className="button-link secondary" to={`/o/${orgId}/calendar?contact=${c.id}`}>Book</Link>}
          {canWrite && plan.enabled("deals") && <button className="secondary" onClick={() => { setPanel(panel === "deal" ? null : "deal"); }}>New {w.lower("deal")}</button>}
          {(canMerge || allows("delete")) && (
            <span className="menu-wrap">
              <button className="secondary icon-only" aria-label="More actions" aria-expanded={menu} onClick={() => setMenu(!menu)}>⋯</button>
              {menu && (
                <span className="menu-pop" role="menu" onMouseLeave={() => setMenu(false)}>
                  {canMerge && <button role="menuitem" onClick={() => { setMenu(false); setPanel("merge"); }}>Merge a duplicate into {c.first_name || "this contact"}…</button>}
                  {allows("delete") && <button role="menuitem" className="danger-text" onClick={async () => {
                    setMenu(false);
                    if (!confirm(`Archive ${name}? They leave every list; their history is kept.`)) return;
                    await api(`${base}/contacts/${contactId}`, { method: "DELETE" });
                    qc.invalidateQueries({ queryKey: [orgId, "contacts"] });
                    navigate(`/o/${orgId}/contacts`);
                  }}>Archive</button>}
                </span>
              )}
            </span>
          )}
        </div>
      </header>
      <ErrorText error={update.error} />
      {canMerge && panel !== "merge" && <DuplicateNotice contactId={c.id} onMerge={() => setPanel("merge")} />}
      {panel === "merge" && <MergePanel into={c} onClose={() => setPanel(null)} onMerged={() => { setPanel(null); refresh(); qc.invalidateQueries({ queryKey: [orgId] }); }} />}
      {panel === "email" && (
        <section className="card"><EmailComposer contactId={c.id} onCancel={() => setPanel(null)} onSent={(tid) => navigate(`/o/${orgId}/inbox/${tid}`)} /></section>
      )}
      {panel === "whatsapp" && <WhatsAppPanel contact={c} sender={wa} onClose={() => setPanel(null)} />}
      {panel === "deal" && (
        <section className="card">
          <h2>New {w.lower("deal")}</h2>
          <NewDeal contactId={c.id} onDone={(id) => { setPanel(null); qc.invalidateQueries({ queryKey: [orgId, "deals"] }); if (id) navigate(`/o/${orgId}/deals/${id}`); }} />
        </section>
      )}
      <div className="contact-layout">
        <div className="contact-main">
          <div className="contact-tabs" role="tablist" aria-label="About this contact">
            {tabs.filter(([, , on]) => on).map(([k, text]) => (
              <button key={k} role="tab" aria-selected={tab === k} className={tab === k ? "on" : ""} onClick={() => setTab(k)}>{text}</button>
            ))}
          </div>
          {tab === "activity" && <ActivityTab contactId={c.id} canWrite={canWrite} />}
          {tab === "conversations" && <ConversationsTab contactId={c.id} />}
          {tab === "deals" && <ContactDeals contactId={c.id} />}
          {tab === "appointments" && <ContactAppointments contactId={c.id} />}
          {tab === "tasks" && <TasksTab contactId={c.id} canWrite={canWrite} />}
        </div>
        <aside className="contact-side">
          <DetailsCard c={c} update={update.mutate} />
          {wa.enabled && wa.active.length > 0 && <WhatsAppConsents contact={c} onChange={refresh} />}
          <CustomFields entity="contact" values={c.custom} canEdit={canWrite} onSave={(custom) => update.mutateAsync({ custom })} />
          {allows("delete") && <ContactPrivacy contactId={c.id} name={name} />}
        </aside>
      </div>
    </>
  );
}

/** TagEditor: the contact's tags, added and removed in place. */
function TagEditor({ tags, canEdit, onSave }: { tags: string[]; canEdit: boolean; onSave: (tags: string[]) => Promise<unknown> }) {
  const [adding, setAdding] = useState(false);
  const [text, setText] = useState("");
  const [error, setError] = useState<unknown>(null);
  const save = async (next: string[]) => { setError(null); try { await onSave(next); setText(""); setAdding(false); } catch (e) { setError(e); } };
  if (tags.length === 0 && !canEdit) return null;
  return (
    <div className="tag-row">
      {tags.map((t) => (
        <span key={t} className="ctag">{t}{canEdit && <button type="button" className="tag-x" aria-label={`Remove tag ${t}`} onClick={() => save(tags.filter((x) => x !== t))}>×</button>}</span>
      ))}
      {canEdit && tags.length < 20 && (adding ? (
        <form className="inline" onSubmit={(e) => { e.preventDefault(); if (text.trim()) save([...tags, text.trim()]); }}>
          <input aria-label="New tag" placeholder="e.g. VIP" maxLength={40} value={text} autoFocus onChange={(e) => setText(e.target.value)} onBlur={() => !text && setAdding(false)} style={{ width: 140 }} />
          <button type="submit" className="secondary small" disabled={!text.trim()}>Add</button>
        </form>
      ) : <button type="button" className="chip add" onClick={() => setAdding(true)}>+ Tag</button>)}
      <ErrorText error={error} />
    </div>
  );
}

/** DetailsCard: one place for the contact's details. */
function DetailsCard({ c, update }: { c: ContactDetail; update: (json: Record<string, unknown>) => void }) {
  const { base, orgId, memberId, allows } = useOrg();
  const qc = useQueryClient();
  const members = useQuery({ queryKey: [orgId, "members"], queryFn: () => api<{ members: Member[] }>(`${base}/members`), enabled: allows("contacts_all") });
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const canWrite = c.can_edit && !allows("readonly");
  const marketing = (c.consents ?? []).find((k) => k.channel === "email" && k.purpose === "marketing");
  if (editing) {
    return (
      <section className="card">
        <form className="form" onSubmit={async (e) => {
          e.preventDefault(); setBusy(true); setError(null);
          const d = new FormData(e.currentTarget);
          try {
            await api(`${base}/contacts/${c.id}`, { method: "PATCH", json: { first_name: str(d, "first_name"), last_name: str(d, "last_name"), email: str(d, "email"), phone: str(d, "phone") } });
            await qc.invalidateQueries({ queryKey: [orgId, "contact", c.id] });
            qc.invalidateQueries({ queryKey: [orgId, "contacts"] });
            setEditing(false);
            toast("Details saved");
          } catch (err) { setError(err); } finally { setBusy(false); }
        }}>
          <h2>Edit details</h2>
          <Field label="First name"><input name="first_name" defaultValue={c.first_name ?? ""} maxLength={100} autoFocus /></Field>
          <Field label="Last name"><input name="last_name" defaultValue={c.last_name ?? ""} maxLength={100} /></Field>
          <Field label="Email"><input name="email" type="email" defaultValue={c.email ?? ""} maxLength={254} placeholder="name@example.com" /></Field>
          <Field label="Phone (with country code)"><input name="phone" type="tel" defaultValue={c.phone ?? ""} maxLength={32} placeholder="+973 3300 0000" /></Field>
          <ErrorText error={error} />
          <div className="inline">
            <button type="submit" disabled={busy}>{busy ? "Saving…" : "Save details"}</button>
            <button type="button" className="link" onClick={() => setEditing(false)}>Cancel</button>
          </div>
        </form>
      </section>
    );
  }
  return (
    <section className="card">
      <div className="card-head"><h2>Details</h2>{canWrite && <button className="secondary small" onClick={() => setEditing(true)}>Edit</button>}</div>
      <dl className="details stacked">
        <dt>Email</dt><dd>{c.email ? <a href={`mailto:${c.email}`}>{c.email}</a> : canWrite ? <button className="link small" onClick={() => setEditing(true)}>Add email</button> : "—"}</dd>
        <dt>Phone</dt><dd>{c.phone ? <a href={`tel:${c.phone}`} dir="ltr">{c.phone}</a> : canWrite ? <button className="link small" onClick={() => setEditing(true)}>Add phone</button> : "—"}</dd>
        <dt>Stage</dt>
        <dd>
          <select value={c.lifecycle_stage} disabled={!canWrite} onChange={(e) => update({ lifecycle_stage: e.target.value })} aria-label="Stage">
            {stages.map((s) => <option key={s} value={s}>{stageLabel[s]}</option>)}
          </select>
        </dd>
        <dt>Owner</dt>
        <dd>
          {allows("contacts_all") && !allows("readonly") ? (
            <select value={c.owner_member_id ?? ""} onChange={(e) => update(e.target.value ? { owner_member_id: e.target.value } : { clear_owner: true })} aria-label="Owner">
              <option value="">Unassigned</option>
              {members.data?.members.filter((m) => m.status === "active" && m.crm_role).map((m) => <option key={m.id} value={m.id}>{m.display_name}</option>)}
            </select>
          ) : c.owner_member_id ? c.owner_name : (
            <>Unassigned {canWrite && <button className="secondary small" onClick={() => update({ owner_member_id: memberId })}>Claim</button>}</>
          )}
        </dd>
        <dt>Language</dt>
        <dd>
          <select aria-label="Language" disabled={!canWrite} value={c.language_source === "staff" ? c.language ?? "" : ""} onChange={(e) => update({ language: e.target.value })}>
            <option value="">{c.language ? `${c.language === "ar" ? "Arabic" : "English"} (${c.language_source === "import" ? "from import" : "from their messages"})` : "Detect from their messages"}</option>
            <option value="ar">Arabic</option>
            <option value="en">English</option>
          </select>
        </dd>
        <dt>Came from</dt><dd>{label(sourceLabel, c.source)}</dd>
        <dt>Added</dt><dd>{dateTime(c.created_at)}</dd>
        <dt>Email marketing</dt>
        <dd>
          {marketing?.status === "granted" ? "Opted in" : "Not opted in"}{" "}
          {canWrite && marketing?.status === "granted" && (
            <button className="link small" onClick={async () => {
              await api(`${base}/contacts/${c.id}/consents`, { method: "PUT", json: { channel: "email", purpose: "marketing", granted: false, note: "Revoked by staff" } });
              qc.invalidateQueries({ queryKey: [orgId, "contact", c.id] });
            }}>Record opt-out</button>
          )}
        </dd>
      </dl>
    </section>
  );
}

/** ActivityTab: log a note, call or meeting, and everything that happened. */
function ActivityTab({ contactId, canWrite }: { contactId: string; canWrite: boolean }) {
  const { base, orgId } = useOrg();
  const qc = useQueryClient();
  const key = [orgId, "contact", contactId, "timeline"];
  const timeline = useInfiniteQuery({
    queryKey: key,
    queryFn: ({ pageParam }: { pageParam: string }) => api<{ activities: Activity[]; next_cursor: string | null }>(`${base}/contacts/${contactId}/timeline?limit=50${pageParam ? `&cursor=${pageParam}` : ""}`),
    initialPageParam: "",
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });
  const [kind, setKind] = useState<"note" | "call" | "meeting">("note");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const items = timeline.data?.pages.flatMap((p) => p.activities) ?? [];
  const prompt = { note: "Write a note for the team…", call: "How did the call go?", meeting: "What was discussed?" }[kind];
  return (
    <>
      {canWrite && (
        <section className="card log-box">
          <div className="segmented" role="group" aria-label="What to log">
            {(["note", "call", "meeting"] as const).map((k) => (
              <button key={k} type="button" className={kind === k ? "" : "secondary"} aria-pressed={kind === k} onClick={() => setKind(k)}>{{ note: "Note", call: "Log a call", meeting: "Log a meeting" }[k]}</button>
            ))}
          </div>
          <form onSubmit={async (e) => {
            e.preventDefault(); setBusy(true); setError(null);
            try {
              await api(`${base}/contacts/${contactId}/notes`, { method: "POST", json: { body: body.trim(), kind } });
              setBody(""); await qc.invalidateQueries({ queryKey: key });
            } catch (err) { setError(err); } finally { setBusy(false); }
          }}>
            <textarea aria-label={prompt} placeholder={prompt} value={body} maxLength={500} rows={3} onChange={(e) => setBody(e.target.value)} dir="auto" />
            <ErrorText error={error} />
            <div className="inline"><span className="muted small">{body.length}/500</span><span className="spacer" /><button type="submit" disabled={busy || !body.trim()}>{busy ? "Saving…" : "Save"}</button></div>
          </form>
        </section>
      )}
      {timeline.isLoading ? <Loading /> : timeline.error ? <ErrorText error={timeline.error} /> : items.length === 0 ? <Empty>Nothing yet.</Empty> : (
        <ul className="timeline card">
          {items.map((a) => (
            <li key={a.id} className={`tl-${a.type.replace(/\./g, "-")}`}>
              {kindIcon[a.type] && <span className="tl-kind" aria-hidden>{kindIcon[a.type]}</span>}
              <span dir="auto">{a.type === "call.logged" ? <b>Call: </b> : a.type === "meeting.logged" ? <b>Meeting: </b> : null}{a.summary}</span>
              <small className="muted">
                {a.actor_name ?? actorLabel[a.actor_type] ?? a.actor_type} · {dateTime(a.occurred_at)}
                {a.thread_id && <> · <Link to={`/o/${orgId}/inbox/${a.thread_id}`}>Open conversation</Link></>}
              </small>
            </li>
          ))}
        </ul>
      )}
      {timeline.hasNextPage && <button className="secondary" disabled={timeline.isFetchingNextPage} onClick={() => timeline.fetchNextPage()}>Show older</button>}
    </>
  );
}

const channelLabel: Record<string, string> = { web: "Website chat", whatsapp: "WhatsApp", email: "Email" };

/** ConversationsTab: every conversation with this contact, on any channel. */
function ConversationsTab({ contactId }: { contactId: string }) {
  const { base, orgId } = useOrg();
  const q = useQuery({ queryKey: [orgId, "threads", "contact", contactId], queryFn: () => api<{ threads: Thread[] }>(`${base}/threads?status=all&contact=${contactId}&limit=50`) });
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorText error={q.error} />;
  const list = q.data!.threads;
  if (list.length === 0) return <Empty>No conversations yet.</Empty>;
  return (
    <ul className="convo-list card">
      {list.map((t) => (
        <li key={t.id}>
          <Link to={`/o/${orgId}/inbox/${t.id}`}>
            <span className="tag">{channelLabel[t.channel] ?? t.channel}</span>
            <span className="grow" dir="auto">{t.subject || t.last_message_preview || "Conversation"}</span>
            <span className="muted small nowrap">{t.status === "closed" ? "Closed · " : ""}{ago(t.last_message_at)}</span>
          </Link>
        </li>
      ))}
    </ul>
  );
}

/** TasksTab: the contact's tasks, ticked off in place. */
function TasksTab({ contactId, canWrite }: { contactId: string; canWrite: boolean }) {
  const { base, orgId } = useOrg();
  const qc = useQueryClient();
  const key = [orgId, "contact", contactId, "tasks"];
  const tasks = useQuery({ queryKey: key, queryFn: () => api<{ tasks: Task[] }>(`${base}/tasks?status=all&assigned=all&contact_id=${contactId}`) });
  const [error, setError] = useState<unknown>(null);
  const done = async (t: Task) => {
    setError(null);
    try { await api(`${base}/tasks/${t.id}`, { method: "PATCH", json: { status: t.status === "open" ? "done" : "open" } }); qc.invalidateQueries({ queryKey: key }); qc.invalidateQueries({ queryKey: [orgId, "tasks"] }); } catch (e) { setError(e); }
  };
  return (
    <section className="card">
      {canWrite && (
        <Form submitLabel="Add task" onSubmit={async (d) => {
          const due = str(d, "due");
          await api(`${base}/tasks`, { method: "POST", json: { contact_id: contactId, title: str(d, "title"), ...(due && { due_at: new Date(due).toISOString() }) } });
          qc.invalidateQueries({ queryKey: key });
          qc.invalidateQueries({ queryKey: [orgId, "tasks"] });
        }}>
          <div className="row">
            <Field label="Task"><input name="title" required maxLength={300} placeholder="e.g. Call back about the quote" /></Field>
            <Field label="Due"><DateTimeInput name="due" /></Field>
          </div>
        </Form>
      )}
      <ErrorText error={error} />
      {tasks.isLoading ? <Loading /> : (tasks.data?.tasks.length ?? 0) === 0 ? <p className="muted small">No tasks.</p> : (
        <ul className="task-list">
          {tasks.data!.tasks.map((t) => {
            const overdue = t.status === "open" && t.due_at && new Date(t.due_at) < new Date();
            return (
              <li key={t.id} className={t.status !== "open" ? "done" : ""}>
                <input type="checkbox" aria-label={`Done: ${t.title}`} checked={t.status === "done"} disabled={!canWrite || t.status === "cancelled"} onChange={() => done(t)} />
                <span className="grow" dir="auto">{t.title}</span>
                {t.due_at && <span className={`small nowrap${overdue ? " overdue" : " muted"}`}>{overdue ? "Overdue · " : "Due "}{dateTime(t.due_at)}</span>}
                <span className="muted small">{t.assignee_name ?? "Unassigned"}</span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

/** DuplicateNotice: other contacts that look like the same person. */
function DuplicateNotice({ contactId, onMerge }: { contactId: string; onMerge: () => void }) {
  const { base, orgId } = useOrg();
  const q = useQuery({ queryKey: [orgId, "contact", contactId, "duplicates"], queryFn: () => api<{ contacts: Contact[] }>(`${base}/contacts/${contactId}/duplicates`), staleTime: 60_000 });
  const list = q.data?.contacts ?? [];
  if (list.length === 0) return null;
  return (
    <p className="notice small inline-card">
      <span>Looks like the same person as {list.slice(0, 2).map((d, i) => <span key={d.id}>{i > 0 && " and "}<Link to={`/o/${orgId}/contacts/${d.id}`}>{contactName(d)}</Link></span>)}{list.length > 2 && ` and ${list.length - 2} more`}.</span>
      <button className="secondary small" onClick={onMerge}>Review and merge</button>
    </p>
  );
}

/** MergePanel: fold a duplicate into this contact. */
function MergePanel({ into, onClose, onMerged }: { into: ContactDetail; onClose: () => void; onMerged: () => void }) {
  const { base, orgId } = useOrg();
  const dups = useQuery({ queryKey: [orgId, "contact", into.id, "duplicates"], queryFn: () => api<{ contacts: Contact[] }>(`${base}/contacts/${into.id}/duplicates`) });
  const [q, setQ] = useState("");
  const found = useQuery({
    queryKey: [orgId, "contacts", "merge-search", q.trim()],
    queryFn: () => api<{ contacts: Contact[] }>(`${base}/contacts?q=${encodeURIComponent(q.trim())}&limit=8`),
    enabled: q.trim().length >= 2,
  });
  const [pick, setPick] = useState<Contact | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const options = (q.trim().length >= 2 ? found.data?.contacts : dups.data?.contacts)?.filter((x) => x.id !== into.id) ?? [];
  const intoName = contactName(into);
  return (
    <section className="card">
      <div className="card-head"><h2>Merge a duplicate into {intoName}</h2><button className="link" onClick={onClose}>Cancel</button></div>
      {!pick ? (
        <>
          <input type="search" placeholder="Search by name, email or phone" value={q} maxLength={100} onChange={(e) => setQ(e.target.value)} aria-label="Find the duplicate" autoFocus />
          {q.trim().length < 2 && <p className="muted small">{options.length ? "These look like the same person:" : "Search for the other contact."}</p>}
          <ul className="pick-list">
            {options.map((d) => (
              <li key={d.id}><button type="button" onClick={() => setPick(d)}><b dir="auto">{contactName(d)}</b><small className="muted">{[d.email, d.phone, stageLabel[d.lifecycle_stage]].filter(Boolean).join(" · ")}</small></button></li>
            ))}
          </ul>
        </>
      ) : (
        <>
          <p><b dir="auto">{contactName(pick)}</b> will be merged into <b dir="auto">{intoName}</b>:</p>
          <ul className="small">
            <li>Their conversations, {`deals`}, appointments, tasks, payments and timeline move to {intoName}.</li>
            <li>Details {intoName} is missing (email, phone, name, custom fields, tags) are copied over. Where both have one, {intoName}'s stays.</li>
            <li>{contactName(pick)} is archived. This can't be undone automatically.</li>
          </ul>
          <ErrorText error={error} />
          <div className="inline">
            <button className="danger" disabled={busy} onClick={async () => {
              setBusy(true); setError(null);
              try { await api(`${base}/contacts/${into.id}/merge`, { method: "POST", json: { from: pick.id } }); onMerged(); toast("Merged"); } catch (e) { setError(e); } finally { setBusy(false); }
            }}>{busy ? "Merging…" : "Merge"}</button>
            <button className="link" onClick={() => setPick(null)}>Choose another</button>
          </div>
        </>
      )}
    </section>
  );
}

function ContactDeals({ contactId }: { contactId: string }) {
  const { base, orgId } = useOrg();
  const plan = useEntitlements();
  const w = useWords();
  const deals = useQuery({ queryKey: [orgId, "deals", "contact", contactId], queryFn: () => api<{ deals: Deal[] }>(`${base}/deals?contact=${contactId}`), enabled: plan.enabled("deals") });
  if (!plan.enabled("deals")) return null;
  if (deals.isLoading) return <Loading />;
  if (deals.error) return <ErrorText error={deals.error} />;
  const list = deals.data!.deals;
  if (list.length === 0) return <Empty>No {w.lower("deal", true)} yet. Use New {w.lower("deal")} above.</Empty>;
  return (
    <ul className="convo-list card">
      {list.map((d) => (
        <li key={d.id}>
          <Link to={`/o/${orgId}/deals/${d.id}`}>
            <span className="grow" dir="auto"><b>{d.title}</b></span>
            <span className="small">{d.status === "open" ? d.stage_name : d.status === "won" ? "Won" : "Lost"}</span>
            <span className="num small">{money(d.amount_minor, d.currency)}</span>
          </Link>
        </li>
      ))}
    </ul>
  );
}

// WhatsApp: the customer's opt-ins (side card) and sending an approved
// template, even if they never wrote first, as long as they agreed to be
// contacted (panel from the header).
function useWhatsAppSender(enabled: boolean) {
  const { base, orgId } = useOrg();
  const conns = useQuery({
    queryKey: [orgId, "channels"],
    queryFn: () => api<{ connections: { id: string; status: string; display_phone: string | null; phone_number_id: string }[] }>(`${base}/channels`),
    enabled,
  });
  const templates = useQuery({ queryKey: [orgId, "wa-templates"], queryFn: () => api<{ templates: WaTemplate[] }>(`${base}/whatsapp/templates`), enabled, staleTime: 60_000 });
  return { enabled, active: (conns.data?.connections ?? []).filter((x) => x.status === "active"), templates: templates.data?.templates ?? [] };
}

function WhatsAppPanel({ contact: c, sender, onClose }: { contact: ContactDetail; sender: ReturnType<typeof useWhatsAppSender>; onClose: () => void }) {
  const { base, orgId } = useOrg();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [connId, setConnId] = useState("");
  const conn = connId || sender.active[0]?.id;
  if (!conn) return null;
  return (
    <section className="card">
      <h2>Message on WhatsApp</h2>
      {sender.active.length > 1 && (
        <Field label="From number">
          <select value={conn} onChange={(e) => setConnId(e.target.value)}>
            {sender.active.map((x) => <option key={x.id} value={x.id}>{x.display_phone ?? x.phone_number_id}</option>)}
          </select>
        </Field>
      )}
      <TemplateSender
        templates={sender.templates.filter((x) => x.connection_id === conn)}
        firstName={c.first_name}
        onCancel={onClose}
        onSend={async (templateId, params) => {
          const r = await api<{ thread_id: string }>(`${base}/contacts/${c.id}/whatsapp`, { method: "POST", json: { connection_id: conn, template_id: templateId, params } });
          qc.invalidateQueries({ queryKey: [orgId, "threads"] });
          navigate(`/o/${orgId}/inbox/${r.thread_id}`);
        }}
      />
    </section>
  );
}

function WhatsAppConsents({ contact: c, onChange }: { contact: ContactDetail; onChange: () => void }) {
  const { base, allows } = useOrg();
  const [error, setError] = useState<unknown>(null);
  const canWrite = c.can_edit && !allows("readonly");
  const consent = (purpose: string) => (c.consents ?? []).find((k) => k.channel === "whatsapp" && k.purpose === purpose)?.status;
  const reachable = (c.identities ?? []).some((i) => i.channel === "whatsapp") || !!c.phone;
  async function setConsent(purpose: "service" | "marketing", granted: boolean) {
    setError(null);
    try {
      await api(`${base}/contacts/${c.id}/consents`, { method: "PUT", json: { channel: "whatsapp", purpose, granted, note: granted ? "Recorded by staff" : "Revoked by staff" } });
      onChange();
    } catch (err) {
      setError(err);
    }
  }
  const row = (purpose: "service" | "marketing", text: string) => {
    const st = consent(purpose);
    return (
      <>
        <dt>{text}</dt>
        <dd>
          {st === "granted" ? "Opted in" : st === "revoked" ? "Opted out" : "Not recorded"}{" "}
          {canWrite && (st === "granted"
            ? <button className="link small" onClick={() => setConsent(purpose, false)}>Record opt-out</button>
            : <button className="link small" onClick={() => confirm(purpose === "marketing" ? "Record that this customer agreed to receive WhatsApp offers and promotions?" : "Record that this customer agreed to be contacted on WhatsApp?") && setConsent(purpose, true)}>Record opt-in</button>)}
        </dd>
      </>
    );
  };
  return (
    <section className="card">
      <h2>WhatsApp</h2>
      <dl className="details stacked">
        {row("service", "Updates & follow-ups")}
        {row("marketing", "Offers & promotions")}
      </dl>
      {!reachable && <p className="muted small">Add their mobile number (with country code) to message them on WhatsApp.</p>}
      <ErrorText error={error} />
    </section>
  );
}

const apptStatus: Record<Appointment["status"], string> = { booked: "Booked", confirmed: "Confirmed", completed: "Attended", no_show: "No-show", cancelled: "Cancelled" };

// ContactAppointments lists the customer's appointments, newest first.
function ContactAppointments({ contactId }: { contactId: string }) {
  const { base, orgId } = useOrg();
  const plan = useEntitlements();
  const enabled = plan.enabled("bookings");
  const list = useQuery({
    queryKey: [orgId, "appointments", "contact", contactId],
    queryFn: () => api<{ appointments: Appointment[] }>(`${base}/appointments?contact_id=${contactId}`),
    enabled,
  });
  const w = useWords();
  if (!enabled) return null;
  const items = [...(list.data?.appointments ?? [])].reverse();
  const upcoming = items.filter((a) => (a.status === "booked" || a.status === "confirmed") && new Date(a.ends_at) > new Date());
  return (
    <section className="card">
      {items.length === 0 ? <p className="muted small">No {w.lower("appointment", true)} yet. <Link to={`/o/${orgId}/calendar?contact=${contactId}`}>Book one</Link>.</p> : (
        <ul className="list">
          {items.slice(0, 8).map((a) => (
            <li key={a.id} className={upcoming.includes(a) ? "" : "muted"}>
              <b>{a.service_name}</b>{" "}
              <span className="small">· {dateTime(a.starts_at)} · {a.resource_name}</span>{" "}
              <span className={`tag appt-status status-${a.status}`}>{apptStatus[a.status]}</span>
              {a.source === "agent" && <span className="tag ai-tag">Booked by AI</span>}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** ContactPrivacy: the customer's data rights (Bahrain PDPL). Admins only. */
function ContactPrivacy({ contactId, name }: { contactId: string; name: string }) {
  const { base, orgId } = useOrg();
  const navigate = useNavigate();
  const [erasing, setErasing] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  async function download() {
    setError(null); setBusy(true);
    try {
      const blob = await apiBlob(`${base}/contacts/${contactId}/export`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `customer-${contactId}.json`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch (e) { setError(e); } finally { setBusy(false); }
  }
  return (
    <section className="card">
      <h2>Customer's data</h2>
      <p className="muted small">If {name} asks what you hold about them, download it all. If they ask you to delete them, erase permanently: their conversations, messages, timeline and consent records are removed; opportunities and appointments stay in your figures without their details. This can't be undone.</p>
      <ErrorText error={error} />
      {!erasing ? (
        <div className="privacy-actions">
          <button className="secondary" disabled={busy} onClick={download}>{busy ? "Preparing…" : "Download their data"}</button>
          <button className="link danger-link" onClick={() => setErasing(true)}>Erase permanently…</button>
        </div>
      ) : (
        <form className="form" onSubmit={async (e) => {
          e.preventDefault(); setError(null); setBusy(true);
          try {
            await api(`${base}/contacts/${contactId}/erase`, { method: "POST", json: { confirm: confirmText.trim() } });
            navigate(`/o/${orgId}/contacts`);
          } catch (err) { setError(err); } finally { setBusy(false); }
        }}>
          <Field label="Type ERASE to permanently delete this customer"><input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} autoComplete="off" maxLength={10} autoFocus /></Field>
          <div className="inline">
            <button type="submit" className="danger" disabled={busy || confirmText.trim() !== "ERASE"}>Erase permanently</button>
            <button type="button" className="link" onClick={() => { setErasing(false); setConfirmText(""); }}>Cancel</button>
          </div>
        </form>
      )}
    </section>
  );
}
